import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RefundIcon from "@mui/icons-material/RequestQuote";

const PaymentSuccessDialog = ({
  open,
  onClose,
  paymentStatus,
  planName,
  redirectToUpgrade,
  disableEscapeKeyDown = false,
}) => {
  const handleContinue = () => {
    onClose();
    window.location.reload(true);
  };

  const handleCloseAndRedirect = () => {
    onClose();
    redirectToUpgrade();
  };

  const getDialogContent = () => {
    switch (paymentStatus) {
      case "PAYMENT_COMPLETED":
        return {
          icon: (
            <CheckCircleIcon
              sx={{
                fontSize: 60,
                color: "#4caf50",
                mb: 2,
                animation: "popIn 0.5s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "80%": {
                    transform: "scale(1.2)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
          ),
          title: "Payment Successful",
          subTitle: `Thank you for purchasing ${planName}. Happy learning!`,
          primaryButtonText: "Continue",
          primaryButtonColor: "primary",
          primaryButtonAction: handleContinue,
        };
      case "PAYMENT_FAILED":
        return {
          icon: (
            <ErrorIcon
              sx={{
                fontSize: 60,
                color: "#f44336",
                mb: 2,
                animation: "popIn 0.5s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "80%": {
                    transform: "scale(1.2)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
          ),
          title: "Payment Failed",
          subTitle:
            "There was an issue processing your payment. Please try again.",
          primaryButtonText: "Close",
          primaryButtonColor: "error",
          primaryButtonAction: handleCloseAndRedirect,
        };
      case "PAYMENT_INITIATED":
        return {
          icon: (
            <HourglassEmptyIcon
              sx={{
                fontSize: 60,
                color: "#ffa726",
                mb: 2,
                animation: "popIn 0.5s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "80%": {
                    transform: "scale(1.2)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
          ),
          title: "Payment Initiated",
          subTitle: `Your payment has been initiated. Please try again to complete the payment for ${planName}.`,
          primaryButtonText: "Close",
          primaryButtonColor: "warning",
          primaryButtonAction: handleCloseAndRedirect,
        };
      case "REFUND_UNDER_PROCESS":
        return {
          icon: (
            <RefundIcon
              sx={{
                fontSize: 60,
                color: "#ff9800",
                mb: 2,
                animation: "popIn 0.5s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "80%": {
                    transform: "scale(1.2)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
          ),
          title: "Refund Under Process",
          subTitle: `Your refund for ${planName} is being processed. Please check back later.`,
          primaryButtonText: "Close",
          primaryButtonColor: "warning",
          primaryButtonAction: handleCloseAndRedirect,
        };
      case "REFUND_COMPLETED":
        return {
          icon: (
            <CheckCircleIcon
              sx={{
                fontSize: 60,
                color: "#4caf50",
                mb: 2,
                animation: "popIn 0.5s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    transform: "scale(0)",
                    opacity: 0,
                  },
                  "80%": {
                    transform: "scale(1.2)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                },
              }}
            />
          ),
          title: "Refund Completed",
          subTitle: `Your refund for ${planName} has been successfully processed.`,
          primaryButtonText: "Close",
          primaryButtonColor: "success",
          primaryButtonAction: handleCloseAndRedirect,
        };
      default:
        return {
          icon: (
            <ErrorIcon
              sx={{
                fontSize: 60,
                color: "#f44336",
                mb: 2,
                animation: "shake 0.5s ease-in-out",
                "@keyframes shake": {
                  "0%": { transform: "translateX(0)" },
                  "25%": { transform: "translateX(-10px)" },
                  "50%": { transform: "translateX(10px)" },
                  "75%": { transform: "translateX(-10px)" },
                  "100%": { transform: "translateX(0)" },
                },
              }}
            />
          ),
          title: "Payment Status",
          subTitle: "Unknown payment status. Please contact support.",
          primaryButtonText: "Close",
          primaryButtonColor: "secondary",
          primaryButtonAction: handleCloseAndRedirect,
        };
    }
  };

  const {
    icon,
    title,
    subTitle,
    primaryButtonText,
    primaryButtonColor,
    primaryButtonAction,
  } = getDialogContent();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      disableEscapeKeyDown={disableEscapeKeyDown}
      aria-labelledby="payment-status-dialog-title"
      PaperProps={{
        sx: {
          borderRadius: 2,
          padding: 2,
          minWidth: 400,
        },
      }}
    >
      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          py: 2,
        }}
      >
        {icon}
        <Typography
          id="payment-status-dialog-title"
          variant="h4"
          sx={{ fontWeight: 600, mb: 1 }}
        >
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {subTitle}
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{
          justifyContent: "center",
          p: 1,
          gap: 2,
        }}
      >
        <Button
          onClick={primaryButtonAction}
          color={primaryButtonColor}
          variant="contained"
          sx={{
            px: 4,
            py: 1,
            fontSize: 16,
            fontWeight: 500,
            textTransform: "none",
            borderRadius: 1,
          }}
        >
          {primaryButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentSuccessDialog;
